import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import { useTranslation } from 'react-i18next';
import './Favoris.css';

const API_BASE_URL = 'https://api.autocasses.com';

// Configuration axios
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

// Intercepteur pour ajouter le token
api.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem('userToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const Favoris = () => {
  const { t } = useTranslation();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' ou 'list'

  // Fonction pour obtenir le texte de condition
  const getConditionText = (condition) => {
    const conditionLower = condition?.toLowerCase();
    switch (conditionLower) {
      case 'neuf': case 'neuve':
        return t('favorites.product.condition.new');
      case 'excellent':
        return t('favorites.product.condition.excellent');
      case 'bon': case 'good':
        return t('favorites.product.condition.good');
      case 'moyen': case 'fair':
        return t('favorites.product.condition.fair');
      case 'mauvais': case 'poor':
        return t('favorites.product.condition.poor');
      default:
        return t('favorites.product.condition.unknown');
    }
  };

  // Fonction pour récupérer les favoris
  const fetchFavorites = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Récupérer les favoris de l'utilisateur
      const response = await api.get('/api/favoris/all');
      
      if (response.data.success && response.data.favoris) {
        // Transformer les données
        const formattedFavorites = response.data.favoris.map(fav => ({
          id: fav.produit.id,
          titre: fav.produit.titre,
          description: fav.produit.description,
          prix: fav.produit.prix,
          etat: fav.produit.etat,
          photos: fav.produit.photos || [],
          user: {
            nom: fav.produit.user?.nom || t('favorites.product.seller')
          },
          city: fav.produit.city,
          region: fav.produit.region,
          categorie: fav.produit.categorie,
          sous_categorie: fav.produit.sous_categorie,
          negociable: fav.produit.negociable,
          echange: fav.produit.echange,
          garantie: fav.produit.garantie,
          createdAt: fav.produit.createdAt,
          favoriId: fav.id // ID du favori
        }));
        
        setFavorites(formattedFavorites);
      } else {
        setFavorites([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des favoris:', error);
      
      if (error.response) {
        switch (error.response.status) {
          case 401:
            setError(t('favorites.error.unauthorized'));
            break;
          case 403:
            setError(t('favorites.error.forbidden'));
            break;
          default:
            setError(t('favorites.error.loading'));
            break;
        }
      } else if (error.request) {
        setError(t('favorites.error.connection'));
      } else {
        setError(t('favorites.error.generic'));
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchFavorites();
  }, []);

  const onRefresh = () => {
    setRefreshing(true);
    fetchFavorites();
  };

  // Fonction pour retirer un produit des favoris
  const removeFromFavorites = async (productId) => {
    if (window.confirm(t('favorites.alerts.removeMessage'))) {
      try {
        // Utiliser la route toggle avec produitId
        await api.post('/api/favoris/toggle', {
          produitId: productId
        });
        
        // Mettre à jour l'état local
        setFavorites(prev => prev.filter(item => item.id !== productId));
        
        alert(t('favorites.alerts.successRemove'));
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        alert(t('favorites.alerts.errorRemove'));
      }
    }
  };

  // Fonction pour vider tous les favoris
  const clearAllFavorites = () => {
    if (window.confirm(t('favorites.alerts.clearAllMessage'))) {
      try {
        // Supprimer chaque favori un par un
        const deletePromises = favorites.map(item =>
          api.post('/api/favoris/toggle', { produitId: item.id })
        );
        
        Promise.all(deletePromises).then(() => {
          setFavorites([]);
          alert(t('favorites.alerts.successClearAll'));
        }).catch(error => {
          console.error('Erreur lors de la suppression:', error);
          alert(t('favorites.alerts.errorClearAll'));
        });
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        alert(t('favorites.alerts.errorClearAll'));
      }
    }
  };

  // Fonction pour formater le prix
  const formatPrice = (price) => {
    if (!price) return 'N/A';
    return new Intl.NumberFormat(t('common.currency') === 'FCFA' ? 'fr-CM' : 'en-US', {
      style: 'currency',
      currency: t('common.currency') === 'FCFA' ? 'XAF' : 'USD',
      minimumFractionDigits: 0
    }).format(price);
  };

  // Fonction pour obtenir la couleur selon l'état
  const getConditionColor = (condition) => {
    const conditionLower = condition?.toLowerCase();
    switch (conditionLower) {
      case 'neuf': case 'neuve':
        return '#10b981';
      case 'excellent':
        return '#3b82f6';
      case 'bon':
        return '#8b5cf6';
      case 'moyen':
        return '#f59e0b';
      case 'mauvais':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  // Naviguer vers les détails du produit
  const navigateToProduct = (product) => {
    window.location.href = `/products/${product.id}`;
  };

  // Naviguer vers la recherche
  const navigateToSearch = () => {
    window.location.href = '/search';
  };

  // Naviguer vers la page d'accueil
  const navigateToHome = () => {
    window.location.href = '/';
  };

  // Rendu d'un produit en mode grille
  const renderGridItem = (item) => (
    <div 
      className="favorites-grid-item"
      onClick={() => navigateToProduct(item)}
      key={item.favoriId || item.id}
    >
      <div className="grid-image-container">
        <img
          src={item.photos?.[0]?.photo_url || 
               item.categorie?.photo_url || 
               'https://images.unsplash.com/photo-1549399542-7e3f8b79c341?w=800&auto=format&fit=crop'}
          alt={item.titre}
          className="grid-image"
          loading="lazy"
        />
        <button
          className="favorite-button active"
          onClick={(e) => {
            e.stopPropagation();
            removeFromFavorites(item.id);
          }}
          aria-label="Remove from favorites"
        >
          <Icon name="Heart" size={20} color="#f68a3b" />
        </button>

      </div>
      
      <div className="grid-content">
        <h3 className="grid-title">
          {item.titre}
        </h3>
        
        <div className="seller-info">
          <Icon name="User" size={12} color="#6b7280" />
          <span className="seller-name">
            {item.user.nom}
          </span>
        </div>
        
        <div className="location-info">
          <Icon name="MapPin" size={12} color="#6b7280" />
          <span className="location-text">
            {item.city || ''} {item.region ? `• ${item.region}` : ''}
          </span>
        </div>
        
        <span className="grid-price">
          {formatPrice(item.prix)}
        </span>
        
        <div className="grid-actions">
          <button 
            className="action-button"
            onClick={(e) => {
              e.stopPropagation();
              alert(t('favorites.alerts.contact'));
            }}
            aria-label="Contact seller"
          >
            <Icon name="MessageCircle" size={16} color="#3b82f6" />
          </button>
          
          <button 
            className="action-button"
            onClick={(e) => {
              e.stopPropagation();
              alert(t('favorites.alerts.share'));
            }}
            aria-label="Share product"
          >
            <Icon name="Share2" size={16} color="#3b82f6" />
          </button>
        </div>
      </div>
    </div>
  );

  // Rendu d'un produit en mode liste
  const renderListItem = (item) => (
    <div 
      className="favorites-list-item"
      onClick={() => navigateToProduct(item)}
      key={item.favoriId || item.id}
    >
      <div className="list-image-container">
        <img
          src={item.photos?.[0]?.photo_url || 
               item.categorie?.photo_url || 
               'https://images.unsplash.com/photo-1549399542-7e3f8b79c341?w=800&auto=format&fit=crop'}
          alt={item.titre}
          className="list-image"
          loading="lazy"
        />
        
        <button
          className="list-favorite-button"
          onClick={(e) => {
            e.stopPropagation();
            removeFromFavorites(item.id);
          }}
          aria-label="Remove from favorites"
        >
          <Icon name="Heart" size={18} color="#ef4444" />
        </button>
      </div>
      
      <div className="list-content">
        <div className="list-header">
          <h3 className="list-title">
            {item.titre}
          </h3>
          <span className="list-price">
            {formatPrice(item.prix)}
          </span>
        </div>
        
        <p className="list-description">
          {item.description || t('favorites.empty.description')}
        </p>
        
        <div className="list-details">
          <div className="list-detail-item">
            <Icon name="User" size={12} color="#6b7280" />
            <span className="list-detail-text">
              {item.user?.nom}
            </span>
          </div>
          
          <div className="list-detail-item">
            <Icon name="MapPin" size={12} color="#6b7280" />
            <span className="list-detail-text">
              {item.city || ''} {item.region ? `• ${item.region}` : ''}
            </span>
          </div>
          
          <div className="list-detail-item">
            <div className="condition-dot" style={{ backgroundColor: getConditionColor(item.etat) }} />
            <span className="list-detail-text">
              {getConditionText(item.etat)}
            </span>
          </div>
        </div>
        
        <div className="list-actions">
          <button 
            className="action-button list-action-button"
            onClick={(e) => {
              e.stopPropagation();
              alert(t('favorites.alerts.contact'));
            }}
          >
            <Icon name="MessageCircle" size={14} color="#3b82f6" />
            <span className="list-action-text">
              {t('favorites.product.contact')}
            </span>
          </button>
          
          <button 
            className="action-button list-action-button"
            onClick={(e) => {
              e.stopPropagation();
              alert(t('favorites.alerts.share'));
            }}
          >
            <Icon name="Share2" size={14} color="#3b82f6" />
            <span className="list-action-text">
              {t('favorites.product.share')}
            </span>
          </button>
        </div>
      </div>
    </div>
  );

  // Écran de chargement
  if (loading && !refreshing) {
    return (
      <div className="favorites-container">
        <div className="center-container">
          <div className="spinner"></div>
          <span className="loading-text">
            {t('favorites.loading')}
          </span>
        </div>
      </div>
    );
  }

  // Écran d'erreur
  if (error && favorites.length === 0) {
    return (
      <div className="favorites-container">
        <div className="center-container">
          <Icon name="AlertTriangle" size={64} color="#6b7280" />
          <h2 className="error-title">
            {t('favorites.error.title')}
          </h2>
          <p className="error-text">{error}</p>
          <Button
            onClick={fetchFavorites}
            className="retry-button"
            iconName="RefreshCw"
            iconPosition="left"
          >
            {t('favorites.actions.retry')}
          </Button>
          <Button
            onClick={navigateToHome}
            variant="outline"
            className="home-button"
            iconName="Home"
            iconPosition="left"
          >
            {t('favorites.actions.home')}
          </Button>
        </div>
      </div>
    );
  }

  // Écran vide
  if (favorites.length === 0) {
    return (
      <div className="favorites-container">
        <div className="center-container">
          <Icon name="Heart" size={64} color="#d1d5db" />
          <h2 className="empty-title">
            {t('favorites.empty.title')}
          </h2>
          <p className="empty-text">
            {t('favorites.empty.description')}
          </p>
          <Button
            onClick={navigateToSearch}
            className="search-button"
            iconName="Search"
            iconPosition="left"
          >
            {t('favorites.actions.search')}
          </Button>
          <Button
            onClick={navigateToHome}
            variant="outline"
            className="home-button"
            iconName="Home"
            iconPosition="left"
          >
            {t('favorites.actions.home')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="favorites-container">
      {/* Contrôles avec bouton de retour */}
      <div className="controls-container">
        <div className="header-left">
          <button 
            className="back-button"
            onClick={() => window.history.back()}
            aria-label="Go back"
          >
            <Icon name="ArrowLeft" size={24} color="#111827" />
          </button>
          <h1 className="header-title">
            {t('favorites.title')}
          </h1>
        </div>
        
        <div className="header-right">
          <div className="view-mode-container">
            <button 
              className={`view-mode-button ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              aria-label="Grid view"
            >
              <Icon name="Grid" size={20} color={viewMode === 'grid' ? '#3b82f6' : '#6b7280'} />
            </button>
            <button 
              className={`view-mode-button ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              aria-label="List view"
            >
              <Icon name="List" size={20} color={viewMode === 'list' ? '#3b82f6' : '#6b7280'} />
            </button>
          </div>
        </div>
      </div>

      {/* Statistiques */}
      {favorites.length > 0 && (
        <div className="stats-container">
          <span className="favorites-count">
            {t('favorites.stats.count', {
              count: favorites.length,
              plural: favorites.length > 1 ? 's' : ''
            })}
          </span>
          <button 
            className="clear-all-button"
            onClick={clearAllFavorites}
            aria-label="Clear all favorites"
          >
            <Icon name="Trash2" size={20} color="#ef4444" />
          </button>
        </div>
      )}

      {/* Liste des favoris */}
      <div className={`favorites-list-container ${viewMode === 'grid' ? 'grid-view' : 'list-view'}`}>
        <h2 className="list-header-text">
          {t('favorites.header.favoritesList')}
        </h2>
        
        {viewMode === 'grid' ? (
          <div className="favorites-grid">
            {favorites.map(item => renderGridItem(item))}
          </div>
        ) : (
          <div className="favorites-list">
            {favorites.map(item => renderListItem(item))}
          </div>
        )}
        
        <div className="list-footer">
          <span className="list-footer-text">
            {t('favorites.stats.count', {
              count: favorites.length,
              plural: favorites.length > 1 ? 's' : ''
            })}
          </span>
        </div>
      </div>

      {/* Bouton flottant pour ajouter plus de favoris */}
      <button
        className="floating-button"
        onClick={navigateToSearch}
        aria-label={t('favorites.actions.search')}
      >
        <Icon name="Search" size={24} color="#fff" />
      </button>
    </div>
  );
};

export default Favoris;